import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { concat, Observable, Subject, timer } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  PostmarkBounceResponses,
  UserTypes,
} from '@site-mate/dashpivot-shared-library';

import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { TeamPermissions, UserWeb } from 'app/users/user.model';

import { inviteEmailStatuses } from './invite-email-statuses';
import { PostmarkWebhookErrorCodes } from './postmark-webhook-error-codes';
import { UserListService } from '../../user-list.service';

@Component({
  selector: 'cc-pending-users-table-row',
  templateUrl: './pending-users-table-row.component.html',
  styleUrls: ['./pending-users-table-row.component.scss'],
})
export class PendingUsersTableRowComponent implements OnInit {
  minuteInMilliseconds = 60000;
  throttleDefaultTime = 15;
  timer$: Observable<number> = timer(0, this.minuteInMilliseconds);
  label: string;
  statusClass: string;
  isEmailBounced: boolean;
  statusPopoverMessage: string;
  buttonPopoverMessage: string;
  throttleRemainingTime: number;
  hierarchy: AppHierarchy;
  hierarchyId: string;
  teamPermission = TeamPermissions;

  @Input() index: number;
  @Input() user: UserWeb;
  @Input() userType: UserTypes;
  @Input() searchTerm = '';
  @Input() canRemoveUser = false;
  @Input() isChecked: boolean;
  @Input() showPermissionToggle: boolean;
  @Input() canUpdateUserPermission: boolean;
  @Input() permissionOptions: string[];

  @Output() check: EventEmitter<UserWeb> = new EventEmitter();
  @Output() onUpdatePermission = new EventEmitter();

  constructor(
    private readonly userListService: UserListService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly toastrService: ToastrService,
    protected readonly segmentService: SegmentService,
    private readonly route: ActivatedRoute,
  ) {}

  private unSubscribe = new Subject<void>();

  ngOnInit(): void {
    this.setUserDetails();
    this.setPopoverMessage();
    this.runRemainingTimeCountDown();
    this.getRouterHierarchy();
  }

  resendInvite() {
    this.userListService
      .resendActivationEmail(this.user.email, this.hierarchy, this.hierarchyId, this.userType)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        () => {
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.PendingUserResendButtonClicked, {
              Context: 'Dashpivot',
              Hierarchy: this.hierarchy,
            }),
            this.segmentService,
          );

          this.toastrService.successByKey('emailInviteSent');
          this.getUserLatestStatus();
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }

  getRouterHierarchy() {
    const { params, data } = this.route.snapshot;

    this.hierarchy = data.hierarchy;
    this.hierarchyId = String(Object.values(params));
  }

  removeEmailSuppression() {
    const removeEmailSuppression$ = this.userListService
      .removeEmailSuppression(this.user.email, this.hierarchy, this.hierarchyId)
      .pipe(takeUntil(this.unSubscribe));

    const resendActivationEmail$ = this.userListService
      .resendActivationEmail(this.user.email, this.hierarchy, this.hierarchyId, this.userType)
      .pipe(takeUntil(this.unSubscribe));

    concat(removeEmailSuppression$, resendActivationEmail$).subscribe({
      complete: () => {
        void EventNotifierService.notify(
          new DashpivotEvent(EventTypes.PendingUserTryAgainButtonClicked, {
            Context: 'Dashpivot',
            Hierarchy: AppHierarchy.Team,
          }),
          this.segmentService,
        );

        this.toastrService.successByKey('emailInviteSent');
        this.getUserLatestStatus();
      },
      error: (error) => {
        this.errorHandler.handle(error);
      },
    });
  }

  getUserLatestStatus() {
    this.userListService
      .getUserByEmail(this.user.email, this.hierarchy, this.hierarchyId)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        ([user]) => {
          this.user.emailStatus = user.emailStatus;
          this.setUserDetails();
          this.runRemainingTimeCountDown();
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }

  setUserDetails() {
    const { emailStatus, activationResendCount } = this.user;
    const inviteEmailStatus = inviteEmailStatuses[1];

    if (!emailStatus) {
      this.label = inviteEmailStatus;
      this.isEmailBounced = false;
      this.throttleRemainingTime = 15;
      return;
    }

    this.isEmailBounced = emailStatus.status !== PostmarkBounceResponses.Sent;

    if (!this.isEmailBounced) {
      this.label = inviteEmailStatuses[activationResendCount] ?? inviteEmailStatus;
      this.statusClass = '';
    } else {
      this.label = PostmarkWebhookErrorCodes[emailStatus.status].label;
      this.statusClass = 'warning-status';
    }
  }

  setThrottleRemainingTime() {
    const { emailStatus } = this.user;
    if (emailStatus) {
      const emaiSentTimeDifference = new Date().getTime() - new Date(emailStatus.sentAt).getTime();
      const minutesAfterEmailSent = emaiSentTimeDifference / this.minuteInMilliseconds;
      this.throttleRemainingTime = Math.ceil(this.throttleDefaultTime - minutesAfterEmailSent);
    }
  }

  runRemainingTimeCountDown() {
    this.setThrottleRemainingTime();

    if (this.throttleRemainingTime > 0) {
      const remainingTimeClock$ = this.timer$.pipe(
        takeWhile(() => this.throttleRemainingTime > 0),
        takeUntil(this.unSubscribe),
      );

      remainingTimeClock$.subscribe(
        () => {
          this.setButtonPopoverMessage();
          this.throttleRemainingTime -= 1;
        },
        (error) => this.errorHandler.handle(error),
        () => {
          if (!this.throttleRemainingTime) {
            this.getUserLatestStatus();
          }
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  setPopoverMessage() {
    if (this.isEmailBounced) {
      this.statusPopoverMessage = PostmarkWebhookErrorCodes[this.user.emailStatus.status]?.popover;
    }
  }

  setButtonPopoverMessage() {
    const timeUnit = this.throttleRemainingTime > 1 ? 'minutes' : 'minute';
    this.buttonPopoverMessage = `You cannot resend another invite email straight away. Please wait ${this.throttleRemainingTime} ${timeUnit}.`;
  }
}
